import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * GlobalFoodOrderListener — se monte au niveau global (App.js).
 * Écoute les messages postMessage du service worker (déclenchés par sw.js à chaque push reçue).
 * Si le user est un livreur (role driver) et que la push est une nouvelle course food (sound=true
 * + url contenant /livreur), redirige immédiatement vers /livreur?order=xxx pour déclencher
 * le modal plein écran + sirène — quelle que soit la page actuelle.
 *
 * Sans ce composant, Jacques recevait seulement la petite notif système silencieuse : le modal
 * de 30s ne s'affichait pas car il n'était pas sur /livreur.
 */
export default function GlobalFoodOrderListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    // Note : on écoute même si l'utilisateur n'est pas encore chargé — filtrage plus loin.

    const onMsg = (e) => {
      const type = e.data?.type;
      // 1) Push reçue en background — sw diffuse le payload à tous les onglets ouverts
      if (type === "nold-push") {
        const p = e.data?.payload;
        if (!p) return;
        const url = p.url || "";
        const isFood = p.sound === true && (url.includes("/livreur") || (p.tag || "").includes("nold-course"));
        if (!isFood) return;
        if (location.pathname === "/livreur") return; // le dashboard va détecter la course dans son polling
        const orderQuery = p.orderId ? `?order=${encodeURIComponent(p.orderId)}` : "";
        toast.info("🛵 Nouvelle course Nold Food — ouverture du dashboard…", { duration: 3000 });
        navigate(`/livreur${orderQuery}`);
        return;
      }
      // 2) Clic sur la notif système — sw envoie un message dédié
      if (type === "nold-notif-click") {
        const p = e.data?.payload;
        if (!p || !p.url) return;
        // Redirige vers l'URL cible (ex: /livreur?order=xxx) même si un onglet est déjà ouvert
        // ailleurs. C'est la correction du bug "le clic sur la notif ne fait rien".
        if (location.pathname + location.search !== p.url) {
          navigate(p.url);
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate, location.pathname, user]);

  return null;
}
